import { useRef, useState } from "react"
import { Grid, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-light-svg-icons"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { tailwindPalette } from "../../../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { CreateRoomResourceForm } from "../../../resourcesTypes"

import { TableArrangementCard } from "./TableArrangementCard"
import { CreateTableArrangementDialog, CreatedTableArrangement } from "./CreateTableArrangementDialog"
import Cafe from "./tableSettingAssets/Cafe.png"
import Cinema from "./tableSettingAssets/Cinema.png"
import Conference from "./tableSettingAssets/Conference.png"
import Group from "./tableSettingAssets/Group.png"
import Herringbone from "./tableSettingAssets/Herringbone.png"
import MeetingRoom from "./tableSettingAssets/Meeting-room.png"
import Negotiation from "./tableSettingAssets/Negotiation.png"
import School from "./tableSettingAssets/School.png"
import UForm from "./tableSettingAssets/U-form.png"

const seatingOptions = [
    { id: 1, labelKey: "tab-table-arrangements-label-meeting-room", fallback: "Meeting room", img: MeetingRoom },
    { id: 2, labelKey: "tab-table-arrangements-label-cinema", fallback: "Cinema", img: Cinema },
    { id: 3, labelKey: "tab-table-arrangements-label-u-form", fallback: "U-form", img: UForm },
    { id: 4, labelKey: "tab-table-arrangements-label-school", fallback: "School", img: School },
    { id: 5, labelKey: "tab-table-arrangements-label-group", fallback: "Group", img: Group },
    { id: 6, labelKey: "tab-table-arrangements-label-negotiation", fallback: "Negotiation", img: Negotiation },
    { id: 7, labelKey: "tab-table-arrangements-label-cafe", fallback: "Cafe", img: Cafe },
    { id: 8, labelKey: "tab-table-arrangements-label-conference", fallback: "Conference", img: Conference },
    { id: 9, labelKey: "tab-table-arrangements-label-herringbone", fallback: "Herringbone", img: Herringbone },
] as const

type CustomArrangement = Readonly<{
    id: number
    label: string
    imageSrc: string
}>

const useStyles = makeStyles()(() => ({
    container: {
        padding: "1rem",
    },
    addCard: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: ".75rem",
        width: "100%",
        height: "100%",
        minHeight: "14rem",
        padding: "1rem",
        cursor: "pointer",
        appearance: "none",
        borderRadius: ".75rem",
        border: `2px dashed ${tailwindPalette.sky[300]}`,
        backgroundColor: "transparent",
        color: tailwindPalette.sky[500],
        transition: "border-color .15s ease, background-color .15s ease",
        "&:hover": {
            borderColor: tailwindPalette.sky[500],
            backgroundColor: tailwindPalette.sky[50],
        },
    },
    addIcon: {
        fontSize: "2rem",
    },
    addLabel: {
        fontWeight: 600,
        textAlign: "center",
        color: tailwindPalette.sky[600],
    },
}))

export function FormTableArrangementsContent() {
    const { classes } = useStyles()
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()

    const [customArrangements, setCustomArrangements] = useState<ReadonlyArray<CustomArrangement>>([])
    const [isDialogOpen, setDialogOpen] = useState(false)
    const nextCustomIdRef = useRef(-1)

    const tableSetups = values.roomDetails.tableSetups ?? []
    const standardId = tableSetups[0]

    const toggleEnabled = (id: number) => {
        // Any card can be toggled. Since the standard is the first element of the array,
        // deselecting it automatically promotes the next selected arrangement to standard.
        const next = tableSetups.includes(id) ? tableSetups.filter((setupId) => setupId !== id) : [...tableSetups, id]
        setFieldValue("roomDetails.tableSetups", next)
    }

    const makeStandard = (id: number) => {
        const next = [id, ...tableSetups.filter((setupId) => setupId !== id)]
        setFieldValue("roomDetails.tableSetups", next)
    }

    const handleCreated = (arrangement: CreatedTableArrangement) => {
        const id = nextCustomIdRef.current
        nextCustomIdRef.current -= 1

        setCustomArrangements((prev) => [...prev, { id, label: arrangement.label, imageSrc: arrangement.imageSrc }])
        // Auto-enable the newly created arrangement so it shows as selected.
        setFieldValue("roomDetails.tableSetups", [...tableSetups, id])
    }

    return (
        <>
            <Grid container spacing="1rem" className={classes.container}>
                {seatingOptions.map((option) => (
                    <Grid item xs={12} sm={6} md={3} key={option.id}>
                        <TableArrangementCard
                            title={
                                <LocalizedStrict id={option.labelKey}>
                                    <span>{option.fallback}</span>
                                </LocalizedStrict>
                            }
                            imageSrc={option.img}
                            imageAlt={option.fallback}
                            isEnabled={tableSetups.includes(option.id)}
                            isStandard={option.id === standardId}
                            onToggle={() => toggleEnabled(option.id)}
                            onMakeStandard={() => makeStandard(option.id)}
                        />
                    </Grid>
                ))}

                {customArrangements.map((custom) => (
                    <Grid item xs={12} sm={6} md={3} key={custom.id}>
                        <TableArrangementCard
                            title={<span>{custom.label}</span>}
                            imageSrc={custom.imageSrc}
                            imageAlt={custom.label}
                            isEnabled={tableSetups.includes(custom.id)}
                            isStandard={custom.id === standardId}
                            onToggle={() => toggleEnabled(custom.id)}
                            onMakeStandard={() => makeStandard(custom.id)}
                        />
                    </Grid>
                ))}

                <Grid item xs={12} sm={6} md={3}>
                    <button type="button" className={classes.addCard} onClick={() => setDialogOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} className={classes.addIcon} />
                        <LocalizedStrict id="tab-table-arrangements-add-new-card">
                            <Typography className={classes.addLabel}>Add new table setting</Typography>
                        </LocalizedStrict>
                    </button>
                </Grid>
            </Grid>

            <CreateTableArrangementDialog
                open={isDialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreated={handleCreated}
            />
        </>
    )
}
